/**
 * Canvas 2D renderer. Flat, sober, readable: strong track/off-track contrast, a
 * world-space grid off-track for speed perception, a vivid car. Static geometry
 * (track fill, edges, start line) is built once as Path2D and redrawn with the
 * camera transform each frame — cheap and crisp at any zoom.
 */

import type { CarConfig, SurfaceId, Track, TrackSample } from '@microligue/shared';
import { CAR_VIEW, CAR_WORLD_SCALE } from '@microligue/shared';
import { HULL_PATH } from '../car/collision.js';
import { CarSpriteCache } from '../car/sprites';
import type { CameraLike } from './camera';
import type { SceneryProp } from './scenery';
import { buildScenery, drawScenery } from './scenery';
import { SURFACE_COLORS, isColdSurface } from './surfaces';

export interface RenderCar {
  x: number;
  y: number;
  angle: number;
  /** Final bumper-car look (server-merged design), rendered via sprite cache. */
  design: CarConfig;
  pseudo: string;
  isSelf: boolean;
  connected: boolean;
  /** Time-attack ghost: drawn translucent, under the real cars. */
  ghost?: boolean;
}

// Style « planche voitures » (2026-07-21) : aplats francs, contours encre,
// bourrelets caoutchouc sombres, ombre plate décalée sous le ruban — le monde
// parle le même langage graphique que les auto-tamponneuses.
const COLORS = {
  offTrack: '#4a9464',
  grid: 'rgba(16, 20, 26, 0.1)',
  ink: '#20222e',
  // Bord de piste caoutchouc (2026-07-23) : gris foncé sobre, cerclé d'encre —
  // les vibreurs crème/rouge juraient avec le décor.
  kerb: '#343a4c',
  checkerA: '#f7f3e8',
  checkerB: '#20222e',
  hullDebug: '#22e5a2',
};

/**
 * Ombre plate du ruban (décalée +4/+4, façon cartes de la planche). Couleur
 * OPAQUE = le gazon assombri : les remplissages par chunk se recouvrent d'un
 * quad (garde anti-couture) et une ombre translucide y doublerait sa densité.
 * Elle ne déborde que de 4 px : jamais jusqu'aux props du décor (dégagés de
 * 14 px minimum).
 */
export const BAND_SHADOW_COLOR = '#3f7e55';
/** Doit dépasser l'emprise extérieure du bourrelet (~kerb/2 + encre ≈ 6.5 px)
 * pour qu'un liseré d'ombre reste visible sous le ruban détouré. */
const BAND_SHADOW_OFFSET = 10;
/** Largeur monde du bourrelet caoutchouc (px) — même emprise que l'ancien
 * vibreur crème/rouge, seule la matière change. */
const KERB_WIDTH = 6;

/**
 * Manteau neigeux autour des sections froides (glace/neige) : le sol et l'ombre
 * du ruban y passent en tons froids — de la verdure contre un ruban gelé
 * n'était pas raccord. À l'approche du changement de surface, le manteau se
 * RESSERRE progressivement vers la piste (fondu géométrique, voir SnowPatch) et
 * finit en langue arrondie qui déborde à peine la ligne — les plaques de fonte
 * du décor (scenery.ts) achèvent de dissoudre la limite. L'ombre du ruban, elle,
 * bascule pile à la frontière, comme la rayure transversale.
 */
export const SNOW_GROUND_COLOR = '#dfeaf2';
/** Ombre plate du ruban sur sol neigeux (voir BAND_SHADOW_COLOR — même rôle). */
export const SNOW_BAND_SHADOW_COLOR = '#c2d2e0';
/** Débord du manteau au-delà du bord de piste (px monde) — couvre la bande où
 * le décor est semé (props jusqu'à ~224 px du bord). */
const SNOW_HALO = 240;
/** Longueur du fondu aux extrémités d'un manteau (px monde le long de la
 * piste). Assez long pour que l'enveloppe des disques décroissants (pente < 1)
 * suive vraiment le rétrécissement au lieu de rester dominée par le premier. */
const SNOW_TAPER_PX = 360;
/** Marge du manteau au-delà du bord de piste au point le plus étroit (px). */
const SNOW_TIP_MARGIN = 16;

const GRID_SPACING = 250;
/** On-track car footprint in world px (the SVG viewBox at CAR_WORLD_SCALE). */
const CAR_DRAW_W = CAR_VIEW.w * CAR_WORLD_SCALE;
const CAR_DRAW_H = CAR_VIEW.h * CAR_WORLD_SCALE;

interface SurfaceRun {
  surface: SurfaceId;
  path: Path2D;
}

/**
 * One slice of the band (~64 samples) with its world bbox: at close zoom only
 * the on-screen chunks are drawn instead of re-tessellating the whole circuit
 * (up to ~4000 samples × 5 passes) every frame (audit multijoueur F7).
 */
interface TrackChunk {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
  /** Whole-chunk band polygon (uniform coat for the override mode). */
  fill: Path2D;
  /** Per-surface sub-polygons tiling the chunk exactly. */
  runs: SurfaceRun[];
  /** Open edge polylines of the chunk. */
  leftEdge: Path2D;
  rightEdge: Path2D;
}

const CHUNK_SAMPLES = 64;
/** Longest side of the pre-rendered far-zoom bitmap (px). */
const TRACK_BITMAP_MAX = 2600;
const BITMAP_PAD = 40;

/**
 * One snow blanket: the centerline polyline of a contiguous cold run, STROKED
 * with a wide round-join line (band width + halo). A stroke, not a filled
 * offset polygon: offsetting by ~300 px folds back on itself inside tight
 * corners and nonzero winding would punch holes there — a stroke paints the
 * union of its segments, hole-free by construction.
 *
 * `taper` : le fondu des extrémités — une chaîne de disques de rayon
 * décroissant (pleine largeur → bord de piste + marge) en SOUS-CHEMINS d'un
 * seul Path2D, rempli en une opération. Des arcs tournant tous dans le même
 * sens ne peuvent pas s'annuler (enroulements de même signe) : l'union est
 * pleine, sans trou, et son enveloppe est lisse — aucun escalier de largeur,
 * contrairement à des strokes par paliers.
 */
interface SnowPatch {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
  path: Path2D;
  width: number;
  closed: boolean;
  taper: Path2D | null;
}

export class Renderer {
  private readonly ctx: CanvasRenderingContext2D;
  private readonly chunks: TrackChunk[];
  private readonly surfaceBoundaries: Path2D;
  /**
   * Far-zoom path: the whole static track pre-rendered ONCE into a bitmap and
   * blitted in a single drawImage when the screen density is at or below the
   * bitmap density (spectacle camera zoomed out — the case where chunk culling
   * cannot help because everything is visible).
   */
  private bitmap: HTMLCanvasElement | null = null;
  private bitmapScale = 0;
  /**
   * Whole-circuit surface flip (admin test tuning). When set, the whole band is
   * painted with this one surface — so the track LOOKS like what it now drives
   * as, instead of misleadingly keeping its real multi-surface colours.
   */
  private overrideSurface: SurfaceId | null = null;
  private dpr = 1;
  /** Échelle de résolution (mode éco) : 1, 0.75 ou 0.5 — voir display.ts. */
  private renderScale = 1;
  /** Grille hors piste (repère de vitesse) — désactivable en mode éco. */
  private gridEnabled = true;
  /** Une esquisse par design, rendue une fois puis blittée (voir sprites.ts). */
  private readonly sprites = new CarSpriteCache();
  /** Mode debug : contour de collision (HULL_PATH) tracé sur chaque voiture. */
  private hullDebug = false;
  /** Path2D du contour exact, construit à la première demande (lazy: pas de
   * Path2D(d) en environnement de test sans DOM). */
  private hullPath: Path2D | null | undefined;

  /** Props du décor (plats, ensemencés du circuit) — voir scenery.ts.
   * Reconstruits à chaque bascule de surface (les props suivent le climat). */
  private scenery: SceneryProp[];
  /** Manteaux neigeux autour des sections froides — voir SnowPatch. */
  private snowPatches: SnowPatch[];

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly track: Track,
  ) {
    // alpha:false — le fond est repeint intégralement chaque frame, le canvas
    // peut donc être opaque : le compositeur n'a plus à fusionner un calque
    // plein écran translucide à chaque frame (gain net sur petit GPU).
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) throw new Error('Canvas 2D non disponible');
    this.ctx = ctx;

    const built = buildTrackChunks(track);
    this.chunks = built.chunks;
    this.surfaceBoundaries = built.boundaries;
    this.scenery = buildScenery(track);
    this.snowPatches = buildSnowPatches(track, null);
    this.rebuildBitmap();
    this.resize();
  }

  /** Reflect the whole-circuit surface flip (null = the track's real surfaces). */
  setSurfaceOverride(surface: SurfaceId | null): void {
    if (this.overrideSurface === surface) return;
    this.overrideSurface = surface;
    // Le climat suit la surface roulée : manteau neigeux et props hivernaux
    // sont rebâtis (même graine — seuls leurs habits changent), puis le bitmap
    // lointain qui les cuit tous. The far-zoom bitmap bakes the fills.
    this.scenery = buildScenery(this.track, surface);
    this.snowPatches = buildSnowPatches(this.track, surface);
    this.rebuildBitmap();
  }

  /** Pre-renders the static track once (fills + boundaries + baked edges). */
  private rebuildBitmap(): void {
    this.bitmap = null;
    this.bitmapScale = 0;
    try {
      const b = this.track.bounds;
      const worldW = b.maxX - b.minX + BITMAP_PAD * 2;
      const worldH = b.maxY - b.minY + BITMAP_PAD * 2;
      const scale = Math.min(1, TRACK_BITMAP_MAX / worldW, TRACK_BITMAP_MAX / worldH);
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.ceil(worldW * scale));
      canvas.height = Math.max(1, Math.ceil(worldH * scale));
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.scale(scale, scale);
      ctx.translate(-b.minX + BITMAP_PAD, -b.minY + BITMAP_PAD);
      // Fond transparent : les ombres translucides du décor se composent sur
      // le gazon (et sa grille) au moment du blit, comme en vectoriel.
      this.drawSnow(
        ctx,
        b.minX - BITMAP_PAD,
        b.minY - BITMAP_PAD,
        b.maxX + BITMAP_PAD,
        b.maxY + BITMAP_PAD,
      );
      drawScenery(
        ctx,
        this.scenery,
        b.minX - BITMAP_PAD,
        b.minY - BITMAP_PAD,
        b.maxX + BITMAP_PAD,
        b.maxY + BITMAP_PAD,
      );
      this.drawTrackVector(ctx, this.chunks, scale);
      this.bitmap = canvas;
      this.bitmapScale = scale;
    } catch {
      // No DOM (headless) or canvas quota: the vector path covers every zoom.
    }
  }

  resize(): void {
    // La densité effective inclut l'échelle éco : moins de pixels rendus, le
    // CSS étire — le levier GPU n°1 sur machine modeste/batterie.
    this.dpr = Math.min(window.devicePixelRatio || 1, 2) * this.renderScale;
    const w = this.canvas.clientWidth || window.innerWidth;
    const h = this.canvas.clientHeight || window.innerHeight;
    this.canvas.width = Math.max(1, Math.round(w * this.dpr));
    this.canvas.height = Math.max(1, Math.round(h * this.dpr));
  }

  /** Densité effective (dpr × échelle éco) — pour cadrer les caméras. */
  get effectiveDpr(): number {
    return this.dpr;
  }

  /** Échelle de résolution du mode éco (1, 0.75, 0.5). No-op si inchangée. */
  setRenderScale(scale: number): void {
    const clamped = Math.min(1, Math.max(0.5, scale));
    if (clamped === this.renderScale) return;
    this.renderScale = clamped;
    this.resize();
  }

  setGridEnabled(enabled: boolean): void {
    this.gridEnabled = enabled;
  }

  /** Affiche/masque le contour de collision (debug) sur chaque voiture. */
  setHullDebug(enabled: boolean): void {
    this.hullDebug = enabled;
  }

  render(cars: RenderCar[], camera: CameraLike): void {
    // devicePixelRatio can change without a resize event (moving the window to
    // another screen, browser zoom on some platforms) — cheap check per frame.
    if (Math.min(window.devicePixelRatio || 1, 2) * this.renderScale !== this.dpr) this.resize();

    const ctx = this.ctx;
    const w = this.canvas.width;
    const h = this.canvas.height;

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = COLORS.offTrack;
    ctx.fillRect(0, 0, w, h);

    camera.applyTransform(ctx, w, h, this.dpr);
    const zoom = camera.zoom * this.dpr;
    const halfW = w / (2 * zoom);
    const halfH = h / (2 * zoom);

    if (this.gridEnabled) {
      this.drawGrid(camera.x - halfW, camera.y - halfH, camera.x + halfW, camera.y + halfH, zoom);
    }
    this.drawTrack(zoom, camera.x - halfW, camera.y - halfH, camera.x + halfW, camera.y + halfH);
    this.drawStartLine();
    // Own car drawn last so it always reads on top of the pack.
    // Ghosts under everyone, own car on top.
    for (const car of cars) if (car.ghost) this.drawCar(car, zoom);
    for (const car of cars) if (!car.isSelf && !car.ghost) this.drawCar(car, zoom);
    for (const car of cars) if (car.isSelf) this.drawCar(car, zoom);
  }

  private drawGrid(minX: number, minY: number, maxX: number, maxY: number, zoom: number): void {
    const ctx = this.ctx;
    ctx.strokeStyle = COLORS.grid;
    ctx.lineWidth = 1 / zoom;
    ctx.beginPath();
    const x0 = Math.floor(minX / GRID_SPACING) * GRID_SPACING;
    const y0 = Math.floor(minY / GRID_SPACING) * GRID_SPACING;
    for (let x = x0; x <= maxX; x += GRID_SPACING) {
      ctx.moveTo(x, minY);
      ctx.lineTo(x, maxY);
    }
    for (let y = y0; y <= maxY; y += GRID_SPACING) {
      ctx.moveTo(minX, y);
      ctx.lineTo(maxX, y);
    }
    ctx.stroke();
  }

  private drawTrack(
    zoom: number,
    viewMinX: number,
    viewMinY: number,
    viewMaxX: number,
    viewMaxY: number,
  ): void {
    const ctx = this.ctx;
    // Far zoom (spectacle camera): one bitmap blit replaces re-tessellating
    // the whole circuit — chunk culling cannot help when all of it is visible.
    if (this.bitmap && zoom <= this.bitmapScale) {
      const b = this.track.bounds;
      ctx.drawImage(
        this.bitmap,
        b.minX - BITMAP_PAD,
        b.minY - BITMAP_PAD,
        this.bitmap.width / this.bitmapScale,
        this.bitmap.height / this.bitmapScale,
      );
      return;
    }
    // Close zoom: draw only the chunks whose bbox intersects the view — and
    // the visible slice of the snow blankets + scenery (baked in the bitmap on
    // the far path). Le manteau passe sous les props : leurs ombres s'y posent.
    this.drawSnow(ctx, viewMinX, viewMinY, viewMaxX, viewMaxY);
    drawScenery(ctx, this.scenery, viewMinX, viewMinY, viewMaxX, viewMaxY);
    const margin = 24;
    const visible: TrackChunk[] = [];
    for (const chunk of this.chunks) {
      if (
        chunk.maxX < viewMinX - margin ||
        chunk.minX > viewMaxX + margin ||
        chunk.maxY < viewMinY - margin ||
        chunk.minY > viewMaxY + margin
      ) {
        continue;
      }
      visible.push(chunk);
    }
    this.drawTrackVector(ctx, visible, zoom);
  }

  /** Manteaux neigeux visibles dans la fenêtre monde donnée (cull par bbox). */
  private drawSnow(
    ctx: CanvasRenderingContext2D,
    minX: number,
    minY: number,
    maxX: number,
    maxY: number,
  ): void {
    if (this.snowPatches.length === 0) return;
    ctx.save();
    ctx.strokeStyle = SNOW_GROUND_COLOR;
    ctx.fillStyle = SNOW_GROUND_COLOR;
    ctx.lineJoin = 'round';
    // Le cœur du manteau s'arrête net (butt) : ses extrémités sont recouvertes
    // par le fondu en disques, qui porte seul la forme de la transition.
    ctx.lineCap = 'butt';
    for (const patch of this.snowPatches) {
      if (patch.maxX < minX || patch.minX > maxX || patch.maxY < minY || patch.minY > maxY) {
        continue;
      }
      ctx.lineWidth = patch.width;
      ctx.stroke(patch.path);
      if (patch.taper) ctx.fill(patch.taper);
    }
    ctx.restore();
  }

  /**
   * Vector pass shared by the live path (culled chunks) and the bitmap bake.
   * `pxPerWorld` = screen pixels per world px (zoom, or the bitmap's scale) —
   * the kerbs keep a readable minimum on screen whatever the zoom.
   */
  private drawTrackVector(
    ctx: CanvasRenderingContext2D,
    chunks: TrackChunk[],
    pxPerWorld: number,
  ): void {
    // Ombre plate du ruban (façon cartes de la planche) : le même polygone,
    // décalé, dans un aplat OPAQUE — les recouvrements de chunks restent
    // invisibles, contrairement à une ombre translucide. Teinte par tronçon de
    // surface : gazon assombri en zone verte, neige assombrie en zone froide.
    // Les quads de recouvrement entre chunks portent la surface de leur
    // échantillon de départ dans les DEUX chunks — même teinte, aucun raccord.
    ctx.save();
    ctx.translate(BAND_SHADOW_OFFSET, BAND_SHADOW_OFFSET);
    if (this.overrideSurface !== null) {
      ctx.fillStyle = isColdSurface(this.overrideSurface)
        ? SNOW_BAND_SHADOW_COLOR
        : BAND_SHADOW_COLOR;
      for (const chunk of chunks) ctx.fill(chunk.fill);
    } else {
      for (const chunk of chunks) {
        for (const run of chunk.runs) {
          ctx.fillStyle = isColdSurface(run.surface) ? SNOW_BAND_SHADOW_COLOR : BAND_SHADOW_COLOR;
          ctx.fill(run.path);
        }
      }
    }
    ctx.restore();

    if (this.overrideSurface !== null) {
      // Whole-circuit flip: one uniform coat, no per-run fills or boundary
      // stripes — the track reads as the single driven surface.
      ctx.fillStyle = SURFACE_COLORS[this.overrideSurface];
      for (const chunk of chunks) ctx.fill(chunk.fill);
    } else {
      for (const chunk of chunks) {
        for (const run of chunk.runs) {
          ctx.fillStyle = SURFACE_COLORS[run.surface];
          ctx.fill(run.path);
        }
      }
      // Transverse stripe at each surface change — the boundary is readable at
      // race speed, not only by comparing fill colors.
      ctx.strokeStyle = 'rgba(16, 20, 26, 0.55)';
      ctx.lineWidth = 5;
      ctx.stroke(this.surfaceBoundaries);
    }

    // Bords : bourrelet caoutchouc gris foncé cerclé d'encre — le ruban reste
    // « détouré » comme les voitures du kit, sans les damiers criards.
    const kerbW = Math.max(KERB_WIDTH, 4 / pxPerWorld);
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.strokeStyle = COLORS.ink;
    ctx.lineWidth = kerbW + 3.5;
    for (const chunk of chunks) {
      ctx.stroke(chunk.leftEdge);
      ctx.stroke(chunk.rightEdge);
    }
    ctx.strokeStyle = COLORS.kerb;
    ctx.lineWidth = kerbW;
    for (const chunk of chunks) {
      ctx.stroke(chunk.leftEdge);
      ctx.stroke(chunk.rightEdge);
    }
  }

  private drawStartLine(): void {
    const ctx = this.ctx;
    const s0 = this.track.samples[0] as TrackSample;
    const lineAngle = Math.atan2(s0.tx, -s0.ty); // along the start line (normal to tangent)
    ctx.save();
    ctx.translate(s0.x, s0.y);
    ctx.rotate(lineAngle);
    const cell = 8;
    const rows = 2;
    const cells = Math.ceil((s0.halfWidth * 2) / cell);
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cells; c++) {
        ctx.fillStyle = (r + c) % 2 === 0 ? COLORS.checkerA : COLORS.checkerB;
        ctx.fillRect(-s0.halfWidth + c * cell, -rows * cell + r * cell + cell, cell, cell);
      }
    }
    ctx.restore();
  }

  private drawCar(car: RenderCar, zoom: number): void {
    const ctx = this.ctx;
    ctx.save();
    ctx.translate(car.x, car.y);
    if (car.ghost) ctx.globalAlpha = 0.4;
    else if (!car.connected) ctx.globalAlpha = 0.55;
    // The SVG car's nose points up (−y in its viewBox); the physics heading
    // 0 points +x — rotate the sprite a quarter turn onto the heading.
    ctx.rotate(car.angle + Math.PI / 2);

    const sprite = this.sprites.get(car.design);
    if (sprite) {
      ctx.drawImage(sprite, -CAR_DRAW_W / 2, -CAR_DRAW_H / 2, CAR_DRAW_W, CAR_DRAW_H);
    } else {
      // Sprite still decoding (a frame or two) or headless: flat silhouette
      // with the design's rubber ring + body, same footprint as the SVG.
      const s = CAR_WORLD_SCALE;
      ctx.fillStyle = car.design.rubber;
      roundRect(ctx, -CAR_DRAW_W / 2 + 4 * s, -CAR_DRAW_H / 2 + 4 * s, 136 * s, 196 * s, 62 * s);
      ctx.fill();
      ctx.fillStyle = car.design.body;
      roundRect(ctx, -CAR_DRAW_W / 2 + 16 * s, -CAR_DRAW_H / 2 + 16 * s, 112 * s, 172 * s, 52 * s);
      ctx.fill();
    }

    if (this.hullDebug) this.drawHull(zoom);
    ctx.restore();

    // Pseudo above the car, constant on-screen size whatever the zoom.
    ctx.save();
    ctx.translate(car.x, car.y);
    const inv = 1 / zoom;
    ctx.scale(inv, inv);
    if (car.ghost) ctx.globalAlpha = 0.55;
    else if (!car.connected) ctx.globalAlpha = 0.55;
    ctx.font = '600 12px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    const label = car.connected ? car.pseudo : `${car.pseudo} (déco.)`;
    ctx.lineWidth = 3;
    ctx.strokeStyle = 'rgba(16, 20, 26, 0.8)';
    ctx.strokeText(label, 0, -16);
    ctx.fillStyle = '#ffffff';
    ctx.fillText(label, 0, -16);
    ctx.restore();
  }

  /**
   * Contour de collision exact (HULL_PATH du module de design, repère
   * 144×204 centré 72,102) tracé dans le repère déjà tourné de la voiture.
   */
  private drawHull(zoom: number): void {
    if (this.hullPath === undefined) {
      try {
        this.hullPath = new Path2D(HULL_PATH);
      } catch {
        this.hullPath = null;
      }
    }
    if (!this.hullPath) return;
    const ctx = this.ctx;
    ctx.save();
    ctx.scale(CAR_WORLD_SCALE, CAR_WORLD_SCALE);
    ctx.translate(-CAR_VIEW.w / 2, -CAR_VIEW.h / 2);
    ctx.strokeStyle = COLORS.hullDebug;
    ctx.lineWidth = 1.5 / (zoom * CAR_WORLD_SCALE);
    ctx.stroke(this.hullPath);
    ctx.restore();
  }
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function buildTrackChunks(track: Track): { chunks: TrackChunk[]; boundaries: Path2D } {
  const samples = track.samples;
  const n = samples.length;
  const leftPts: [number, number][] = new Array(n);
  const rightPts: [number, number][] = new Array(n);
  for (let i = 0; i < n; i++) {
    const s = samples[i] as TrackSample;
    // Normal = perp(tangent) in y-down space.
    const nx = -s.ty;
    const ny = s.tx;
    leftPts[i] = [s.x - nx * s.halfWidth, s.y - ny * s.halfWidth];
    rightPts[i] = [s.x + nx * s.halfWidth, s.y + ny * s.halfWidth];
  }

  // Transverse marks at every surface boundary (left edge → right edge).
  const boundaries = new Path2D();
  for (let i = 0; i < n; i++) {
    const cur = samples[i] as TrackSample;
    const prev = samples[(i - 1 + n) % n] as TrackSample;
    if (cur.surface !== prev.surface) {
      const l = leftPts[i] as [number, number];
      const r = rightPts[i] as [number, number];
      boundaries.moveTo(l[0], l[1]);
      boundaries.lineTo(r[0], r[1]);
    }
  }

  const chunks: TrackChunk[] = [];
  for (let start = 0; start < n; start += CHUNK_SAMPLES) {
    const len = Math.min(CHUNK_SAMPLES, n - start);
    const indices: number[] = [];
    // …+1 shared sample to tile, +1 more quad of overlap so antialiasing can
    // never open a hairline seam between the fills of two adjacent chunks.
    for (let k = 0; k <= len + 1; k++) indices.push((start + k) % n);

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    const leftEdge = new Path2D();
    const rightEdge = new Path2D();
    indices.forEach((idx, k) => {
      const l = leftPts[idx] as [number, number];
      const r = rightPts[idx] as [number, number];
      minX = Math.min(minX, l[0], r[0]);
      minY = Math.min(minY, l[1], r[1]);
      maxX = Math.max(maxX, l[0], r[0]);
      maxY = Math.max(maxY, l[1], r[1]);
      if (k === 0) {
        leftEdge.moveTo(l[0], l[1]);
        rightEdge.moveTo(r[0], r[1]);
      } else {
        leftEdge.lineTo(l[0], l[1]);
        rightEdge.lineTo(r[0], r[1]);
      }
    });

    chunks.push({
      minX,
      minY,
      maxX,
      maxY,
      fill: bandPolygon(indices, leftPts, rightPts),
      runs: chunkSurfaceRuns(indices, samples, leftPts, rightPts),
      leftEdge,
      rightEdge,
    });
  }

  return { chunks, boundaries };
}

/**
 * Per-surface sub-polygons of one chunk: quad i→i+1 wears the surface of its
 * START sample (the same convention as the whole game). Consecutive runs share
 * their boundary sample, so they tile the chunk with no gap.
 */
function chunkSurfaceRuns(
  indices: number[],
  samples: TrackSample[],
  leftPts: [number, number][],
  rightPts: [number, number][],
): SurfaceRun[] {
  const runs: SurfaceRun[] = [];
  let runStart = 0;
  for (let k = 1; k < indices.length; k++) {
    const prevSurface = (samples[indices[k - 1] as number] as TrackSample).surface;
    const isLast = k === indices.length - 1;
    const nextSurface = isLast ? null : (samples[indices[k] as number] as TrackSample).surface;
    if (isLast || nextSurface !== prevSurface) {
      runs.push({
        surface: prevSurface,
        path: bandPolygon(indices.slice(runStart, k + 1), leftPts, rightPts),
      });
      runStart = k;
    }
  }
  return runs;
}

/**
 * Manteaux neigeux : pour chaque série contiguë (circulaire) d'échantillons
 * froids, la polyligne centrale des quads concernés — du premier échantillon
 * froid au bord aval du dernier quad froid (quad i→i+1 = surface de son
 * échantillon de DÉPART, la convention de tout le jeu). Le cœur est un stroke
 * large (voir SnowPatch) ; largeur = demi-bande max de la série + halo. Les
 * extrémités d'une série ouverte réservent SNOW_TAPER_PX de polyligne pour le
 * fondu : des disques de rayon décroissant (smoothstep) jusqu'au bord de piste
 * + marge, la pointe posée pile sur l'échantillon frontière.
 */
function buildSnowPatches(track: Track, overrideSurface: SurfaceId | null): SnowPatch[] {
  const samples = track.samples;
  const n = samples.length;
  if (n === 0) return [];
  const cold = (i: number): boolean =>
    isColdSurface(overrideSurface ?? (samples[i] as TrackSample).surface);

  const patchFrom = (indices: number[], closed: boolean): SnowPatch => {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    let halfMax = 0;
    const pts: [number, number][] = indices.map((idx) => {
      const s = samples[idx] as TrackSample;
      if (s.halfWidth > halfMax) halfMax = s.halfWidth;
      minX = Math.min(minX, s.x);
      minY = Math.min(minY, s.y);
      maxX = Math.max(maxX, s.x);
      maxY = Math.max(maxY, s.y);
      return [s.x, s.y];
    });
    const width = 2 * (halfMax + SNOW_HALO);
    const pad = width / 2;
    const len = pts.length;

    // Fondu : points de polyligne réservés à chaque extrémité (série ouverte
    // assez longue seulement — un îlot froid minuscule garde sa coupe nette).
    const taperPts = closed
      ? 0
      : Math.min(Math.round(SNOW_TAPER_PX / track.step), Math.floor((len - 2) / 2));
    const tapered = !closed && taperPts >= 4;
    const from = tapered ? taperPts : 0;
    const to = tapered ? len - 1 - taperPts : len - 1;

    const path = new Path2D();
    for (let k = from; k <= to; k++) {
      const p = pts[k] as [number, number];
      if (k === from) path.moveTo(p[0], p[1]);
      else path.lineTo(p[0], p[1]);
    }
    if (closed) path.closePath();

    let taper: Path2D | null = null;
    if (tapered) {
      taper = new Path2D();
      const rMin = halfMax + SNOW_TIP_MARGIN;
      const rMax = width / 2;
      const disc = (p: [number, number], r: number): void => {
        // moveTo d'abord : sans lui, arc() relierait les sous-chemins entre eux.
        (taper as Path2D).moveTo(p[0] + r, p[1]);
        (taper as Path2D).arc(p[0], p[1], r, 0, Math.PI * 2);
      };
      for (let j = 0; j <= taperPts; j += 3) {
        const t = 1 - j / taperPts; // 1 au raccord avec le cœur, 0 à la pointe
        const ease = t * t * (3 - 2 * t);
        const r = rMin + (rMax - rMin) * ease;
        disc(pts[taperPts - j] as [number, number], r);
        disc(pts[len - 1 - taperPts + j] as [number, number], r);
      }
      // La pointe exactement sur l'échantillon frontière, quel que soit le pas.
      disc(pts[0] as [number, number], rMin);
      disc(pts[len - 1] as [number, number], rMin);
    }

    return {
      minX: minX - pad,
      minY: minY - pad,
      maxX: maxX + pad,
      maxY: maxY + pad,
      path,
      width,
      closed,
      taper,
    };
  };

  // Débuts de série : échantillon froid dont le prédécesseur ne l'est pas.
  const starts: number[] = [];
  for (let i = 0; i < n; i++) {
    if (cold(i) && !cold((i - 1 + n) % n)) starts.push(i);
  }
  if (starts.length === 0) {
    // Aucun début : circuit uniformément froid (anneau fermé) ou sans froid.
    if (!cold(0)) return [];
    const all: number[] = [];
    for (let i = 0; i < n; i++) all.push(i);
    return [patchFrom(all, true)];
  }

  const patches: SnowPatch[] = [];
  for (const start of starts) {
    const indices: number[] = [start];
    let i = start;
    while (cold((i + 1) % n)) {
      i = (i + 1) % n;
      indices.push(i);
    }
    // Bord aval du dernier quad froid — la coupe tombe pile sur la frontière.
    indices.push((i + 1) % n);
    patches.push(patchFrom(indices, false));
  }
  return patches;
}

function bandPolygon(
  indices: number[],
  leftPts: [number, number][],
  rightPts: [number, number][],
): Path2D {
  const path = new Path2D();
  const firstIdx = indices[0] as number;
  const firstLeft = leftPts[firstIdx] as [number, number];
  path.moveTo(firstLeft[0], firstLeft[1]);
  for (let i = 1; i < indices.length; i++) {
    const p = leftPts[indices[i] as number] as [number, number];
    path.lineTo(p[0], p[1]);
  }
  for (let i = indices.length - 1; i >= 0; i--) {
    const p = rightPts[indices[i] as number] as [number, number];
    path.lineTo(p[0], p[1]);
  }
  path.closePath();
  return path;
}
